// LandingPage Component - Modern landing page for ShreemAstro
import React, { useState, useEffect } from 'react';
import { Box, Button, Card, Typography, Grid, Container, Sheet, Stack, Chip } from '@mui/joy';
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TimelineIcon from '@mui/icons-material/Timeline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExploreIcon from '@mui/icons-material/Explore';
import { supabase } from '../utils/supabaseClient';

interface SubscriptionPlan {
  id: string;
  plan_name: 'silver' | 'gold';
  billing_cycle: 'monthly' | 'yearly';
  region: 'india' | 'international';
  price_inr: number;
  price_usd: number;
  display_name: string;
  description: string;
  features_json: string[];
}

interface LandingPageProps {
  onStartTrial: () => void;
  onSelectPlan: (plan: 'trial' | 'silver' | 'gold') => void;
  onLogin: () => void;
}

export default function LandingPage({ onStartTrial, onSelectPlan, onLogin }: LandingPageProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [region] = useState<'india' | 'international'>('india'); // Default to India

  useEffect(() => {
    fetchPlans();
  }, []);

  // Auto-scroll to #pricing if redirected from expired trial overlay
  useEffect(() => {
    if (sessionStorage.getItem('scrollToPricing')) {
      sessionStorage.removeItem('scrollToPricing');
      // Small delay to let the page render first
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .eq('region', region)
        .eq('billing_cycle', 'yearly') // Show yearly plans on landing page
        .order('display_order');

      if (error) throw error;
      setPlans(data || []);
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
  };

  const silverPlan = plans.find(p => p.plan_name === 'silver');
  const goldPlan = plans.find(p => p.plan_name === 'gold');

  const features = [
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
      title: 'Objective 0-100% Scoring',
      description: 'First app with numerical planet strength, house power & life area scores. No vague "good" or "moderate"',
      gradient: 'linear-gradient(135deg, #FFA500 0%, #FF6B6B 100%)'
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
      title: 'ShreemAstro AI Assistant',
      description: 'Powered by GPT-5. 24/7 instant answers to any astrology question with structured chart context - like having a personal astrologer',
      gradient: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)'
    },
    {
      icon: <FamilyRestroomIcon sx={{ fontSize: 40 }} />,
      title: 'Combined Family Analysis',
      description: 'Get all family members\' individual + combined readings with 0-100% scores. Check spouse/partner compatibility using 20+ ancient and modern parameters',
      gradient: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)'
    },
    {
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      title: '8,700+ Rules & Combinations',
      description: 'Root-cause analysis with time-sensitive Yogas, Doshas, and Predictions. Get active insights and targeted solutions for your current life phase',
      gradient: 'linear-gradient(135deg, #FF1493 0%, #C71585 100%)'
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 40 }} />,
      title: 'Proactive Notifications',
      description: 'Daily/weekly forecasts + opportunity windows. Get alerts for career, wealth, health, relationships - before they happen',
      gradient: 'linear-gradient(135deg, #8B0000 0%, #DC143C 100%)'
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      title: '16 Divisional Charts (D1-D60)',
      description: 'Deep planetary analysis across 16 varga charts matched to life areas - career uses D10, marriage D9, education D24. Most apps only analyze D1',
      gradient: 'linear-gradient(135deg, #CD853F 0%, #8B4513 100%)'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Life Event Probability',
      description: 'AI-powered 1-year forecast showing when key events (marriage, career change, property purchase) are most likely to manifest - with 84% accuracy',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)'
    },
    {
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      title: 'Horoscope Dashboard Meter',
      description: 'Interactive dashboard showing real-time house scores, Bhava Bala strength, and active dasha impacts - see your life areas at a glance',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)'
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
      title: 'Personal Muhurta Advisor',
      description: 'Find the perfect timing to start important activities (business launch, property purchase, surgery) using classical electional astrology + natal chart analysis',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)'
    },
    {
      icon: <ExploreIcon sx={{ fontSize: 40 }} />,
      title: 'Horoscope-Linked Vastu Chart',
      description: '8-direction Vastu analysis based on your natal planet placements - get personalized room recommendations for career, health, wealth, and relationships',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #E65100 100%)'
    }
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Sign Up (30 Seconds)',
      description: 'Magic link email or Sign in with Google. Add up to 3 family members from 50,000+ Indian cities',
      color: '#FFA500'
    },
    {
      step: '2',
      title: 'Instant Chart (<500ms)',
      description: 'Lightning-fast client-side calculations. See your 0-100% scores immediately - no waiting',
      color: '#FF1493'
    },
    {
      step: '3',
      title: 'Ask ShreemAstro Bot Anything',
      description: 'Get personalized predictions, SWOT analysis, remedies, and proactive opportunity alerts',
      color: '#8B0000'
    }
  ];

  const testimonials = [
    {
      name: 'Priya',
      location: '32, Bangalore',
      text: 'Finally, numbers instead of vague statements! Other apps said my career was "good". ShreemAstro showed me 78% - now I know exactly where I stand and can track changes.',
      rating: 5
    },
    {
      name: 'Rahul',
      location: '28, Mumbai',
      text: 'GPT-5 is like having an astrologer 24/7. Asked 5 questions about my Saturn dasha - got instant, detailed answers. Would\'ve cost ₹1,500 in consultations elsewhere.',
      rating: 5
    },
    {
      name: 'Anita',
      location: '45, Delhi', 
      text: 'Perfect for our family. 4 family members for ₹299/month vs ₹500 per person elsewhere. Saved ₹1,700/month!',
      rating: 5
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #FFFEF9 0%, #FFF5E6 50%, #FFFEF9 100%)',
    }}>
      {/* Navigation Bar */}
      <Sheet
        variant="solid"
        color="neutral"
        sx={{
          py: 2,
          px: { xs: 2, md: 4 },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(184, 134, 11, 0.2)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <img src="/Logo.png" alt="ShreemAstro" style={{ width: 56, height: 56 }} />
              <Typography 
                level="h3" 
                sx={{ 
                  fontFamily: 'Amita, serif',
                  color: '#8B0000',
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '2rem' },
                }}
              >
                ShreemAstro
                <Box component="sup" sx={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0, color: 'rgba(184,48,80,1)' }}>™</Box>
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              color="neutral"
              onClick={onLogin}
              sx={{ 
                borderColor: '#B8860B',
                color: '#B8860B',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
                  color: '#fff',
                  borderColor: 'transparent'
                }
              }}
            >
              Login
            </Button>
          </Box>
        </Container>
      </Sheet>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid xs={12} md={6}>
            <Stack spacing={3}>
              <Chip 
                variant="soft" 
                sx={{ 
                  alignSelf: 'flex-start',
                  background: 'linear-gradient(135deg, #FFA500 0%, #FF6B6B 100%)',
                  color: '#fff',
                  fontWeight: 600
                }}
              >
                🌟 Ancient Wisdom Powered by Intelligent Tech
              </Chip>
              
              <Typography 
                level="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.3,
                  pb: 1,
                  background: 'linear-gradient(135deg, #8B0000 0%, #DC143C 50%, #FF1493 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                The First Vedic Astrology App with Objective Scoring
              </Typography>
              
              <Typography level="h3" sx={{ color: '#8B0000', fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
                Stop Guessing. Start Knowing.
              </Typography>
              
              <Typography level="body-lg" sx={{ color: 'text.secondary', fontSize: '1.125rem', lineHeight: 1.7 }}>
                While other apps give you vague predictions like "good" or "moderate", ShreemAstro shows you exactly 
                how strong each area of your life is with <strong>0-100% scores</strong>. Stay ahead with alerts, catch favorable periods, 
                apply targeted remedies.
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <Button 
                  size="lg" 
                  onClick={onStartTrial}
                  sx={{ 
                    background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.125rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B8860B 0%, #8B6914 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(184, 134, 11, 0.3)'
                    }
                  }}
                  endDecorator={<ArrowForwardIcon />}
                >
                  Start Free Trial
                </Button>
                <Button 
                  size="lg" 
                  variant="outlined"
                  sx={{ 
                    borderColor: '#FF1493',
                    color: '#FF1493',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.125rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF1493 0%, #C71585 100%)',
                      color: '#fff',
                      borderColor: 'transparent'
                    }
                  }}
                  onClick={() => {
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  View Pricing
                </Button>
              </Stack>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography level="h3" sx={{ color: '#8B0000', fontWeight: 700 }}>8,700+</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Prediction Rules</Typography>
                </Box>
                <Box sx={{ width: 1, height: 40, bgcolor: 'divider' }} />
                <Box>
                  <Typography level="h3" sx={{ color: '#FF1493', fontWeight: 700 }}>40+</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Classical Yogas</Typography>
                </Box>
                <Box sx={{ width: 1, height: 40, bgcolor: 'divider' }} />
                <Box>
                  <Typography level="h3" sx={{ color: '#DAA520', fontWeight: 700 }}>GPT-5</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>24/7 Personalized AI Astrologer</Typography>
                </Box>
                <Box sx={{ width: 1, height: 40, bgcolor: 'divider' }} />
                <Box>
                  <Typography level="h3" sx={{ color: '#FFA500', fontWeight: 700 }}>100%</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Objective Scoring</Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>
          
          <Grid xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(139, 0, 0, 0.2)',
              }}
            >
              <img 
                src={`/shreemastro.png?v=${Date.now()}`}
                alt="ShreemAstro Chart" 
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 245, 230, 0.5) 100%)'
      }}>
        <Container maxWidth="lg">
          <Stack spacing={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                level="h2" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(135deg, #8B0000 0%, #FF1493 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                What Makes ShreemAstro Different?
              </Typography>
              <Typography level="body-lg" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                Astrology made personal and easy + proactive guidance + critical family alerts + life event finder - all for ₹75/family member
              </Typography>
            </Box>
            
            <Grid container spacing={3} rowSpacing={6}>
              {features.map((feature, idx) => (
                <Grid key={idx} xs={12} sm={6} md={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      transition: 'all 0.3s ease',
                      borderColor: 'transparent',
                      '&:hover': {
                        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-8px)',
                        borderColor: 'rgba(184, 134, 11, 0.3)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        mb: 2
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography level="h4" sx={{ mb: 1, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={6}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              level="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              How It Works
            </Typography>
            <Typography level="body-lg" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              Get started in three simple steps
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {howItWorks.map((step, idx) => (
              <Grid key={idx} xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}CC 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '2rem',
                      fontWeight: 700,
                      mx: 'auto',
                      mb: 3,
                      boxShadow: `0 8px 24px ${step.color}40`
                    }}
                  >
                    {step.step}
                  </Box>
                  <Typography level="h4" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  <Typography level="body-md" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ 
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, rgba(255, 245, 230, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%)'
      }}>
        <Container maxWidth="lg">
          <Stack spacing={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                level="h2" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(135deg, #FF1493 0%, #8B0000 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Choose Your Plan
              </Typography>
              <Typography level="body-lg" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                Try our platform risk-free or choose an annual plan with the best value
              </Typography>
            </Box>
            
            <Grid container spacing={3} justifyContent="center">
              {/* 7-Day Trial Plan */}
              <Grid xs={12} sm={6} md={4}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    borderColor: 'rgba(34, 197, 94, 0.3)',
                    borderWidth: 2,
                    '&:hover': {
                      boxShadow: '0 16px 48px rgba(34, 197, 94, 0.2)',
                      transform: 'translateY(-8px)',
                      borderColor: '#22C55E'
                    }
                  }}
                >
                  <Chip
                    variant="solid"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      right: 24,
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      color: '#fff',
                      fontWeight: 700
                    }}
                  >
                    🎁 TRY FREE
                  </Chip>
                  <Typography level="h3" sx={{ mb: 1, color: '#16A34A', fontWeight: 700 }}>
                    7-Day Trial
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                    Experience all features - No credit card required
                  </Typography>
                  
                  <Typography level="h2" sx={{ mb: 3, fontWeight: 800 }}>
                    ₹0
                    <Typography level="body-sm" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                      / 7 days
                    </Typography>
                  </Typography>
                  
                  <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm" sx={{ fontWeight: 700 }}>Self & Spouse + 2 family members (4 charts)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm">Family members chart analysis</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm">Life Areas Dashboard & Remedies</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm">Proactive Daily/Weekly Forecast Alerts</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ fontSize: 20, color: '#DC2626', mt: 0.25, display: 'flex', alignItems: 'center' }}>❌</Box>
                      <Typography level="body-sm" sx={{ textDecoration: 'line-through', color: 'text.tertiary' }}>AI chat questions (not available)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm">Yogas & predictions</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm">Divisional charts</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm">Future Event Predictor</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: '#16A34A', mt: 0.25 }} />
                      <Typography level="body-sm">Horoscope linked Vastu</Typography>
                    </Box>
                  </Stack>
                  
                  <Button 
                    size="lg"
                    onClick={() => onSelectPlan('trial')}
                    sx={{ 
                      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
                        boxShadow: '0 8px 24px rgba(22, 163, 74, 0.3)'
                      }
                    }}
                  >
                    Start Free Trial
                  </Button>
                </Card>
              </Grid>

              {/* Silver Plan */}
              {silverPlan && (
                <Grid xs={12} sm={6} md={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      borderColor: 'rgba(192, 192, 192, 0.3)',
                      '&:hover': {
                        boxShadow: '0 16px 48px rgba(192, 192, 192, 0.2)',
                        transform: 'translateY(-8px)',
                        borderColor: '#C0C0C0'
                      }
                    }}
                  >
                    <Typography level="h3" sx={{ mb: 1, color: '#C0C0C0', fontWeight: 700 }}>
                      Silver
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                      ₹62/month per person - Perfect for families
                    </Typography>
                    
                    <Typography level="h2" sx={{ mb: 3, fontWeight: 800 }}>
                      ₹{silverPlan.price_inr.toLocaleString()}
                      <Typography level="body-sm" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                        / year
                      </Typography>
                    </Typography>
                    
                    <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Self & Spouse + 2 family members</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Family members chart analysis</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Life Areas Dashboard & Remedies</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Proactive Daily/Weekly Forecast Alerts</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">3 general AI chat questions/month</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">3 life area analyses/month</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Yogas & predictions</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Divisional charts</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Future Event Predictor</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#C0C0C0', mt: 0.25 }} />
                        <Typography level="body-sm">Horoscope linked Vastu</Typography>
                      </Box>
                    </Stack>
                    
                    <Button 
                      variant="outlined" 
                      size="lg"
                      onClick={() => onSelectPlan('silver')}
                      sx={{ 
                        borderColor: '#C0C0C0',
                        color: '#C0C0C0',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)',
                          color: '#fff',
                          borderColor: 'transparent'
                        }
                      }}
                    >
                      Select Plan
                    </Button>
                  </Card>
                </Grid>
              )}
              
              {/* Gold Plan */}
              {goldPlan && (
                <Grid xs={12} sm={6} md={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      borderColor: 'rgba(218, 165, 32, 0.5)',
                      borderWidth: 2,
                      '&:hover': {
                        boxShadow: '0 20px 60px rgba(218, 165, 32, 0.3)',
                        transform: 'translateY(-8px)',
                      }
                    }}
                  >
                    <Chip
                      variant="solid"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 24,
                        background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                        color: '#fff',
                        fontWeight: 700
                      }}
                    >
                      ⭐ POPULAR
                    </Chip>
                    
                    <Typography level="h3" sx={{ mb: 1, color: '#DAA520', fontWeight: 700 }}>
                      Gold
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                      ₹97/month per person - Premium features for serious users
                    </Typography>
                    
                    <Typography level="h2" sx={{ mb: 3, fontWeight: 800 }}>
                      ₹{goldPlan.price_inr.toLocaleString()}
                      <Typography level="body-sm" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                        / year
                      </Typography>
                    </Typography>
                    
                    <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm" sx={{ fontWeight: 700 }}>All of Silver +</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm" sx={{ fontWeight: 700 }}>Self & Spouse + 4 family members</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm">Marriage Compatibility Analysis</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm">Combined Family Score Card</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm">Personal Muhurat Advisor</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm">Life Event Finder</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm">6 general AI chat questions/month</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#DAA520', mt: 0.25 }} />
                        <Typography level="body-sm">6 life area analyses/month</Typography>
                      </Box>
                    </Stack>
                    
                    <Button 
                      size="lg"
                      onClick={() => onSelectPlan('gold')}
                      sx={{ 
                        background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #B8860B 0%, #8B6914 100%)',
                          boxShadow: '0 8px 24px rgba(184, 134, 11, 0.3)'
                        }
                      }}
                    >
                      Select Plan
                    </Button>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={6}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              level="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #8B0000 0%, #DC143C 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              What Our Users Say
            </Typography>
            <Typography level="body-lg" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              Trusted by thousands for accurate Vedic astrology insights
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {testimonials.map((testimonial, idx) => (
              <Grid key={idx} xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 3,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} sx={{ fontSize: 20, color: '#DAA520' }} />
                    ))}
                  </Box>
                  
                  <Typography level="body-md" sx={{ mb: 2, lineHeight: 1.7, fontStyle: 'italic' }}>
                    "{testimonial.text}"
                  </Typography>
                  
                  <Box sx={{ mt: 'auto' }}>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {testimonial.name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {testimonial.location}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* Footer CTA */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #8B0000 0%, #DC143C 50%, #FF1493 100%)',
          color: '#fff',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography level="h2" sx={{ mb: 2, fontWeight: 700, color: '#fff' }}>
            You Deserve Better Than Vague Predictions
          </Typography>
          <Typography level="body-lg" sx={{ mb: 1, opacity: 0.95, fontSize: '1.25rem' }}>
            Other apps tell you your career is "good" or "moderate".<br/>
            <strong>ShreemAstro shows you it's 78%</strong> - objective, trackable, trustworthy.
          </Typography>
          <Typography level="body-md" sx={{ mb: 4, opacity: 0.9 }}>
            Start your 7-day free trial today. Full features, no credit card required.
          </Typography>
          <Button 
            size="lg"
            onClick={onStartTrial}
            sx={{ 
              background: '#fff',
              color: '#8B0000',
              px: 6,
              py: 2,
              fontSize: '1.125rem',
              fontWeight: 700,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)'
              }
            }}
            endDecorator={<ArrowForwardIcon />}
          >
            Get Started Free
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Sheet
        variant="solid"
        color="neutral"
        sx={{
          py: 4,
          px: { xs: 2, md: 4 },
          background: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              © 2026 ShreemAstro. All rights reserved. | Professional Vedic Astrology powered by AI
            </Typography>
          </Box>
        </Container>
      </Sheet>

      {/* Floating Register Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 20, md: 32 },
          right: { xs: 20, md: 32 },
          zIndex: 999,
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)', boxShadow: '0 8px 24px rgba(139, 0, 0, 0.4)' },
            '50%': { transform: 'scale(1.05)', boxShadow: '0 12px 32px rgba(255, 20, 147, 0.6)' }
          }
        }}
      >
        <Button
          size="lg"
          onClick={() => {
            // Scroll to top and trigger registration
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => onStartTrial(), 500);
          }}
          sx={{
            background: 'linear-gradient(135deg, #8B0000 0%, #DC143C 50%, #FF1493 100%)',
            color: '#fff',
            px: { xs: 3, md: 4 },
            py: { xs: 1.5, md: 2 },
            fontSize: { xs: '0.9rem', md: '1rem' },
            fontWeight: 700,
            borderRadius: '999px',
            animation: 'pulse 2s ease-in-out infinite',
            boxShadow: '0 8px 24px rgba(139, 0, 0, 0.4)',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 12px 32px rgba(255, 20, 147, 0.7)',
              animation: 'none'
            }
          }}
          endDecorator={<ArrowForwardIcon />}
        >
          <Typography sx={{ fontWeight: 700, color: '#fff' }}>
            First Time User? Click to Register
          </Typography>
        </Button>
      </Box>
    </Box>
  );
}
